/*
 **	Push Notifications Service
 **	OMNI IQ Mobile (c) 2025 Silver Falls Capital. All Rights Reserved
 */

import { PushNotifications } from '@capacitor/push-notifications';
import type { Token, PushNotificationSchema, ActionPerformed } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';

export interface NotificationPayload {
  title: string;
  body: string;
  data?: any;
}

class PushNotificationService {
  private initialized = false;
  private token: string | null = null;

  async initialize(): Promise<void> {
    if (this.initialized) return;
    
    // Only initialize on native platforms
    if (!Capacitor.isNativePlatform()) {
      console.log('📱 Push Notifications: Web platform detected, skipping native setup');
      return;
    }

    try {
      // Request permissions
      console.log('📱 Push Notifications: Requesting permissions...');
      const permission = await PushNotifications.requestPermissions();
      
      if (permission.receive === 'granted') {
        console.log('📱 Push Notifications: Permission granted');
        await this.setupListeners();
        await PushNotifications.register();
      } else {
        console.warn('📱 Push Notifications: Permission denied');
      }

      this.initialized = true;
    } catch (error) {
      console.error('📱 Push Notifications: Initialization failed:', error);
    }
  }

  private async setupListeners(): Promise<void> {
    // Registration success
    PushNotifications.addListener('registration', (token: Token) => {
      console.log('📱 Push Notifications: Registration token:', token.value);
      this.token = token.value;
      this.sendTokenToServer(token.value);
    });

    // Registration error
    PushNotifications.addListener('registrationError', (error: any) => {
      console.error('📱 Push Notifications: Registration error:', error);
    });

    // Notification received while app is in foreground
    PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
      console.log('📱 Push Notifications: Received in foreground:', notification);
      this.handleForegroundNotification(notification);
    });

    // Notification tapped (app opened from notification)
    PushNotifications.addListener('pushNotificationActionPerformed', (notification: ActionPerformed) => {
      console.log('📱 Push Notifications: Notification tapped:', notification);
      this.handleNotificationTap(notification);
    });
  }

  private async sendTokenToServer(token: string): Promise<void> {
    try {
      // TODO: Send token to your backend server for storing with user profile
      console.log('📱 Push Notifications: Token to send to server:', token);
      
      // Example API call (implement according to your backend)
      /*
      const response = await fetch('/api/push-tokens', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userToken}`
        },
        body: JSON.stringify({
          token: token,
          platform: Capacitor.getPlatform(),
          deviceId: await Device.getId()
        })
      });
      */
    } catch (error) {
      console.error('📱 Push Notifications: Failed to send token to server:', error);
    }
  }

  private handleForegroundNotification(notification: PushNotificationSchema): void {
    // Handle notification when app is in foreground
    console.log('📱 Push Notifications: Handling foreground notification');
    
    // Show custom in-app notification or alert
    // You might want to show a toast or in-app banner here
    
    // Example: Show browser notification on web
    if (!Capacitor.isNativePlatform() && 'Notification' in window) {
      new Notification(notification.title || 'OMNI IQ Alert', {
        body: notification.body,
        icon: '/omniiq_logo.png',
        tag: 'omniiq-notification'
      });
    }
  }

  private handleNotificationTap(actionPerformed: ActionPerformed): void {
    // Handle when user taps notification to open app
    const notification = actionPerformed.notification;
    console.log('📱 Push Notifications: User tapped notification:', notification);

    // Navigate to relevant screen based on notification data
    if (notification.data) {
      this.navigateFromNotification(notification.data);
    }
  }

  private navigateFromNotification(data: any): void {
    // Navigate to relevant screen based on notification payload
    console.log('📱 Push Notifications: Navigating from notification data:', data);

    // Example navigation logic (implement with your router)
    try {
      if (data.type === 'device_alarm') {
        // Navigate to specific device
        window.location.hash = `/devices?deviceId=${data.deviceId}`;
      } else if (data.type === 'escalation') {
        // Navigate to escalation details
        window.location.hash = `/logs?filter=error`;
      } else {
        // Default navigation
        window.location.hash = '/devices';
      }
    } catch (error) {
      console.error('📱 Push Notifications: Navigation failed:', error);
    }
  }

  // Public methods
  async getRegistrationToken(): Promise<string | null> {
    await this.initialize();
    return this.token;
  }

  async checkPermissions(): Promise<any> {
    return await PushNotifications.checkPermissions();
  }

  async requestPermissions(): Promise<any> {
    return await PushNotifications.requestPermissions();
  }

  // Method to be called when user logs out
  async clearToken(): Promise<void> {
    try {
      // TODO: Remove token from server
      console.log('📱 Push Notifications: Clearing token from server');
      
      this.token = null;
    } catch (error) {
      console.error('📱 Push Notifications: Failed to clear token:', error);
    }
  }

  // Check if push notifications are supported
  isSupported(): boolean {
    return Capacitor.isNativePlatform() || ('Notification' in window);
  }
}

// Singleton instance
export const pushNotificationService = new PushNotificationService();